import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Nav, Navbar } from 'react-bootstrap'
import { LogoImage } from '../logo'
import { LogoImageType } from 'app/modules/admin/features/settings/appearance/types'
import { Link, useNavigate } from 'react-router-dom'
import { getPageUrl } from 'app/modules/shared/routing'
import { debounce } from 'lodash'
import { usePageScroll } from 'app/modules/shared/helpers'
import { MobileMenuCloseButton, MobileMenuOpenedOverlay } from './mobile-assets'
import { routeState } from 'app/modules/shared'
import Search from '../appbar/search-bar'
import { useResponsive } from 'app/modules/shared/hooks/use-responsive'

export default function AppbarLayout({ children, isAdmin }: { children: React.ReactNode; isAdmin?: boolean }) {
  const pageState = useContext(routeState)
  const navigate = useNavigate()
  const prevScrollPos = useRef(0)
  const appbarRef = useRef<HTMLElement>(null)
  const { isMobile } = useResponsive([{ maxWidth: 992, isMobile: true }], { isMobile: false })

  const [menuExpanded, setMenuExpanded] = useState(false)
  const [navbarVisible, setNavbarVisible] = useState(isMobile)

  const toggleMenu = () => setMenuExpanded((prev) => !prev)

  const calcAppbarVisibleHeight = useCallback(() => {
    if (appbarRef.current) {
      const { bottom } = appbarRef.current.getBoundingClientRect()
      const visibleHeight = isMobile && !navbarVisible ? 0 : Math.max(0, bottom)
      document.documentElement.style.setProperty('--appbar-visible-height', `${visibleHeight}px`)
    }
  }, [isMobile, navbarVisible])

  // Handler for hiding appbar on scroll
  const handleScroll = useCallback(
    debounce(() => {
      if (isMobile) {
        const currentScrollPos = window.scrollY
        const prevPos = prevScrollPos.current
        const scrollUp = prevPos > currentScrollPos
        const scrollThresholdReached = prevPos - currentScrollPos > 50
        prevScrollPos.current = currentScrollPos

        setNavbarVisible((scrollUp && scrollThresholdReached) || currentScrollPos < 10)
      }

      document.body.classList.toggle('scrolled', window.scrollY > 0)
    }, 100),
    [isMobile]
  )

  // Handle hiding appbar on scroll and closing mobile menu with backswipe
  useEffect(() => {
    const onScroll = () => {
      handleScroll()
      calcAppbarVisibleHeight()
    }

    calcAppbarVisibleHeight()
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
      handleScroll.cancel()
    }
  }, [handleScroll, calcAppbarVisibleHeight])

  // Close the accordion menu on navigate events
  useEffect(() => {
    setMenuExpanded(false)
  }, [pageState])

  // Handle filter panel offset on mobile when appbar is hidden/shown
  useEffect(() => {
    calcAppbarVisibleHeight()
  }, [calcAppbarVisibleHeight])

  useEffect(() => {
    const handleBackSwipe = (event: PopStateEvent) => {
      if (menuExpanded) {
        event.preventDefault()
        toggleMenu()
        // Block the browser from going back to the previous history point
        navigate(0)
      }
    }

    window.addEventListener('popstate', handleBackSwipe)
    return () => window.removeEventListener('popstate', handleBackSwipe)
  }, [])

  // Disable page scroll if menu on mobile is opened, and enable it back if closed
  usePageScroll(!menuExpanded)

  return (
    <>
      <Navbar
        ref={appbarRef}
        collapseOnSelect
        expand="lg"
        expanded={menuExpanded}
        className={`appbar ${!isAdmin ? 'horizontal-shadow' : ''} ${!navbarVisible ? 'appbar-mobile-hidden' : ''}`}
      >
        <div className="appbar-container">
          <Link to={getPageUrl('scan') as string} aria-label="Logo - Scan">
            <LogoImage type={LogoImageType.TopMenu} className="logo appbar-logo" />
          </Link>

          {isAdmin && (
            <div className="admin-panel-title">
              ADMIN <br className="hide-mobile" /> PANEL
            </div>
          )}

          <div className="appbar-icons-wrapper hide-desktop">
            {!isAdmin && <Search />}
            <Navbar.Toggle aria-controls="appbar-nav" aria-label="Toggle menu" onClick={toggleMenu}>
              {menuExpanded ? (
                <i className="ds3-icon ds3-chevron-up appbar-toggler-icon" />
              ) : (
                <i className="ds3 ds3-bars appbar-toggler-icon" />
              )}
            </Navbar.Toggle>
          </div>
        </div>

        <Navbar.Collapse className="appbar-collapse" id="appbar-nav">
          <Nav className="h-100">{children}</Nav>
        </Navbar.Collapse>
      </Navbar>

      <MobileMenuOpenedOverlay isOpened={menuExpanded} />
      <MobileMenuCloseButton isOpened={menuExpanded} toggleMenu={toggleMenu} />
    </>
  )
}
